import type { ShipmentExim } from '@/services/api/shipments';

/**
 * Client-specific additional-note fields, resolved on the FE.
 *
 * Which recon fields a client captures is decided HERE by client NAME, not by a
 * per-client definitions table + endpoint — that table existed and was
 * deliberately removed (nest-logistic-service `dd54a5d4`). The values flow into
 * `shipments.additional_notes` under these keys, which is what the export reads,
 * so `key` MUST match the export template's note keys exactly.
 *
 * Two screens read this list and must not drift:
 *   1. the create wizard, which captures whatever is known at intake, and
 *   2. the shipment detail panel, which renders the rest as empty slots ops can
 *      fill once, later.
 *
 * (2) is why an empty slot needs no database row: a key with no value is simply
 * absent, and this list is what tells the panel the slot should exist at all.
 */

export type NoteFieldType = 'Text' | 'Number' | 'Date';

/** `BOTH` = both EXIM directions (Export AND Import), NOT Umum/GENERAL. */
export type NoteFieldDir = 'IMPORT' | 'EXPORT' | 'BOTH';

/** A rendered additional-note field. FE-local — there is no definitions table. */
export interface ClientNoteField {
  id: string;
  key: string;
  label: string;
  type: NoteFieldType;
  dir: NoteFieldDir;
  required: boolean;
  description: string | null;
}

export type StaticNoteField = Pick<
  ClientNoteField,
  'key' | 'label' | 'type' | 'dir' | 'required'
>;

/**
 * AGL Patimban (any env / client id — matched by name). The one rule for
 * "is this a Patimban shipment": it picks the Informasi Tambahan fields below
 * and the fixed Dokumen Resmi slots (PLAN-SHIPMENT-DOCS-DYNAMIC R1).
 */
export const isPatimbanClient = (name?: string | null): boolean =>
  !!name && name.toLowerCase().includes('patimban');

/**
 * Label → key, matching the server's `^[a-z0-9]+(?:_[a-z0-9]+)*$`. Shared by
 * the free-form note keys and the ad-hoc document keys (`doc_` + this), so the
 * key ops sees before saving is the one the server derives.
 */
export const toNoteKey = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export const STATIC_CLIENT_NOTE_FIELDS: {
  match: (clientName: string) => boolean;
  fields: StaticNoteField[];
}[] = [
  {
    // None of these prefill on Umum or for a non-Patimban client.
    match: isPatimbanClient,
    fields: [
      { key: 'agl_route_code', label: 'AGL Route Code', type: 'Text', dir: 'BOTH', required: false },
      { key: 'agl_order_code', label: 'AGL Order Code', type: 'Text', dir: 'BOTH', required: false },
      // FO Number is EXPORT-only (AGL, 2026-09): an import booking carries none,
      // and the import CSV no longer has the column.
      { key: 'fo_number', label: 'FO Number', type: 'Text', dir: 'EXPORT', required: false },
      // BL Number is IMPORT-only and deliberately NOT required: on an empty
      // return it is only known when the trip ends. Ops leaves it blank here and
      // fills it once from the shipment detail panel. Forcing a value at intake
      // would mean a made-up one, and the later fill is write-once — a
      // placeholder would freeze permanently.
      { key: 'bl_number', label: 'BL Number', type: 'Text', dir: 'IMPORT', required: false },
    ],
  },
];

/**
 * The additional-note fields to render for a client + direction. A `BOTH` field
 * applies to both EXIM directions (Export/Import) but NOT to Umum/GENERAL; a
 * direction-specific one only under its exim. So on an Umum booking none of
 * these recon fields show.
 */
export const resolveClientNoteFields = (
  clientName: string,
  exim: ShipmentExim
): ClientNoteField[] =>
  STATIC_CLIENT_NOTE_FIELDS.filter((g) => g.match(clientName))
    .flatMap((g) => g.fields)
    .filter((f) => f.dir === exim || (f.dir === 'BOTH' && exim !== 'GENERAL'))
    .map((f) => ({
      id: `static:${f.key}`,
      key: f.key,
      label: f.label,
      type: f.type,
      dir: f.dir,
      required: f.required,
      description: null,
    }));
